import { createSlice } from '@reduxjs/toolkit'

import {
  completeSubscriptionOrderAPI,
  cancelSubscriptionOrderAPI,
  getSubscriptionOrderAPI,
  postSubscriptionOrderAPI,
  sendSubscriptionOrderRemainderSmsAPI,
  sendSubscriptionOrderSmsAPI,
  startSubscriptionOrderAPI,
  terminateSubscriptionOrderAPI,
} from '@/api/subscriptionOrderApi'

import { getLocale, messages } from './i18nReducer'
import { setApiErrorPopup } from './popupReducer'

export const NAME = 'subscription-order'

// @ts-ignore
export const fetchSubscriptionOrder = orderId => async (dispatch, getState) => {
  const isPending = getIsPending(getState())

  if (isPending) return

  dispatch(pending())

  try {
    const { dealer_comment, ...order } = await getSubscriptionOrderAPI(orderId)
    const parsedOrder = { dealer_comment: '', existing_dealer_comment: dealer_comment, ...order }
    dispatch(success(parsedOrder))
  } catch (error) {
    dispatch(setApiErrorPopup('Could not load order'))
    dispatch(failure('Could not load order'))
  }
}

// @ts-ignore
export const saveSubscriptionOrder = orderId => async (dispatch, getState) => {
  const isPending = getIsPostPending(getState())

  if (isPending) return

  dispatch(postPending())

  try {
    const { existing_dealer_comment, ...orderOld } = getSubscriptionOrder(getState())
    const updatedOrder = {
      ...orderOld,
      dealer_comment: existing_dealer_comment
        ? existing_dealer_comment.concat(' ', orderOld.dealer_comment)
        : orderOld.dealer_comment,
    }

    const { dealer_comment, ...order } = await postSubscriptionOrderAPI({
      orderId,
      order: updatedOrder,
    })
    const parsedOrder = { dealer_comment: '', existing_dealer_comment: dealer_comment, ...order }
    dispatch(postSuccess(parsedOrder))
  } catch (error) {
    dispatch(setApiErrorPopup('Could not update order'))
    dispatch(postFailure('Could not update order'))
  }
}

// @ts-ignore
export const startSubscriptionOrder = orderId => async (dispatch, getState) => {
  const isPending = getIsPostPending(getState())

  if (isPending) return

  dispatch(postPending())

  try {
    const orderOld = getSubscriptionOrder(getState())
    const { dealer_comment, ...orderNew } = await startSubscriptionOrderAPI({
      orderId,
      order: orderOld,
    })
    const parsedOrder = { dealer_comment: '', existing_dealer_comment: dealer_comment, ...orderNew }

    dispatch(postSuccess(parsedOrder))
  } catch (error) {
    dispatch(setApiErrorPopup('Could not update order'))
    dispatch(postFailure('Could not update order'))
  }
}

// @ts-ignore
export const completeSubscriptionOrder = orderId => async (dispatch, getState) => {
  const isPending = getIsPostPending(getState())

  if (isPending) return

  dispatch(postPending())

  try {
    const orderOld = getSubscriptionOrder(getState())
    const { dealer_comment, ...orderNew } = await completeSubscriptionOrderAPI({
      orderId,
      order: orderOld,
    })
    const parsedOrder = { dealer_comment: '', existing_dealer_comment: dealer_comment, ...orderNew }

    dispatch(postSuccess(parsedOrder))
  } catch (error) {
    dispatch(setApiErrorPopup('Could not update order'))
    dispatch(postFailure('Could not update order'))
  }
}

// @ts-ignore
export const terminateSubscriptionOrder = orderId => async (dispatch, getState) => {
  const isPending = getIsPostPending(getState())

  if (isPending) return

  dispatch(postPending())

  try {
    const orderOld = getSubscriptionOrder(getState())
    const { dealer_comment, ...orderNew } = await terminateSubscriptionOrderAPI({
      orderId,
      order: orderOld,
    })
    const parsedOrder = { dealer_comment: '', existing_dealer_comment: dealer_comment, ...orderNew }

    dispatch(postSuccess(parsedOrder))
  } catch (error) {
    dispatch(setApiErrorPopup('Could not update order'))
    dispatch(postFailure('Could not update order'))
  }
}

// @ts-ignore
export const cancelSubscriptionOrder = orderId => async (dispatch, getState) => {
  const isPending = getIsPostPending(getState())

  if (isPending) return

  dispatch(postPending())

  try {
    const orderOld = getSubscriptionOrder(getState())
    const { dealer_comment, ...orderNew } = await cancelSubscriptionOrderAPI({
      orderId,
      order: orderOld,
    })
    const parsedOrder = { dealer_comment: '', existing_dealer_comment: dealer_comment, ...orderNew }

    dispatch(postSuccess(parsedOrder))
  } catch (error) {
    dispatch(setApiErrorPopup('Could not cancel order'))
    dispatch(postFailure('Could not cancel order'))
  }
}

// @ts-ignore
export const sendDeviceOrderSms = () => async (dispatch, getState) => {
  const isPending = getIsPostPending(getState())

  if (isPending) return

  dispatch(postPending())

  try {
    const { dealer_comment, ...order } = getSubscriptionOrder(getState())
    const aliasId = getSelectedAlias(getState())
    const message = getSmsInputText(getState())
    const messages = await sendSubscriptionOrderSmsAPI({ orderId: order.id, aliasId, message })
    const parsedOrder = {
      dealer_comment: '',
      existing_dealer_comment: dealer_comment,
      ...order,
      sms_log: messages,
    }

    dispatch(postSuccess(parsedOrder))
    dispatch(setSmsInputText(''))
  } catch (error) {
    dispatch(setApiErrorPopup('Could not send sms'))
    dispatch(postFailure('Could not send sms'))
  }
}

// @ts-ignore
export const sendSubscriptionOrderRemainderSms = () => async (dispatch, getState) => {
  const isPending = getIsPostPending(getState())

  if (isPending) return

  dispatch(postPending())

  try {
    const { dealer_comment, ...order } = getSubscriptionOrder(getState())
    const messages = await sendSubscriptionOrderRemainderSmsAPI(order.id)
    const parsedOrder = {
      dealer_comment: '',
      existing_dealer_comment: dealer_comment,
      ...order,
    }
    dispatch(
      postSuccess({
        ...parsedOrder,
        sms_log: messages,
      })
    )
    dispatch(setSmsInputText(''))
  } catch (error) {
    dispatch(setApiErrorPopup('Could not send sms'))
    dispatch(postFailure('Could not send sms'))
  }
}

const initialState = {
  pending: false,
  isDataLoaded: false,
  error: undefined,
  postPending: false,
  postError: undefined,
  postSuccess: false,
  order: {},
  originalOrder: {},
  smsInputTest: '',
  selectedAlias: 0,
}

const { actions, reducer } = createSlice({
  name: NAME,
  initialState,
  reducers: {
    pending: state => {
      state.error = undefined
      state.pending = true
    },
    success: (state, { payload: order }) => {
      state.pending = false
      state.isDataLoaded = true
      state.order = order
      state.originalOrder = { ...order }
    },
    failure: (state, { payload: error }) => {
      state.pending = false
      state.isDataLoaded = true
      state.error = error
    },
    postPending: state => {
      state.postPending = true
      state.postError = undefined
      state.postSuccess = false
    },
    postSuccess: (state, { payload: order }) => {
      state.postPending = false
      state.postSuccess = true
      state.order = order
    },
    postFailure: (state, { payload: error }) => {
      state.postPending = false
      state.postError = error
      state.postSuccess = false
    },
    updateOrderProp: (state, { payload }) => {
      const { prop, value } = payload
      state.order = {
        ...state.order,
        [prop]: value,
      }
    },
    setSmsInputText: (state, { payload: text }) => {
      state.smsInputTest = text
    },
    setAlias: (state, { payload: alias }) => {
      state.selectedAlias = alias
    },
    reset: () => ({
      ...initialState,
    }),
  },
})

export const {
  pending,
  success,
  failure,
  postPending,
  postSuccess,
  postFailure,
  updateOrderProp,
  setSmsInputText,
  setAlias,
  reset,
} = actions
export default reducer

// @ts-ignore
const getState = state => state[NAME]
// @ts-ignore
export const getIsPending = state => getState(state).pending
// @ts-ignore
export const getError = state => getState(state).error
// @ts-ignore
export const getIsDataLoaded = state => getState(state).isDataLoaded

// @ts-ignore
export const getIsPostPending = state => getState(state).postPending
// @ts-ignore
export const getPostError = state => getState(state).postError
// @ts-ignore
export const getIsPosted = state => getState(state).postSuccess
// @ts-ignore
export const getSelectedAlias = state => getState(state).selectedAlias
// @ts-ignore
export const getSmsInputText = state => getState(state).smsInputTest

// @ts-ignore
export const getSubscriptionOrder = state => {
  const order = getState(state).order
  return {
    ...order,
    terminationTypeText:
      order.termination_type_id !== null
        ? // @ts-ignore
          messages[getLocale(state)][`TYPE_TERMINATION/${order.termination_type_id}`]
        : undefined,
  }
}

// @ts-ignore
export const getOriginalSubscriptionOrder = state => {
  const order = getState(state).originalOrder
  return {
    ...order,
    terminationTypeText:
      order.termination_type_id !== null
        ? // @ts-ignore
          messages[getLocale(state)][`TYPE_TERMINATION/${order.termination_type_id}`]
        : undefined,
  }
}
